import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { useContent } from '../hooks/useContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAREERS_FALLBACK = {
  hero_title_line1: 'Build Your Career',
  hero_title_highlight: 'with ARVARON',
  hero_description: "Join a team that's shaping tomorrow's global economy",
  perks: [
    { num: '01', title: 'Growth', description: 'Accelerate your career across diverse industries and markets' },
    { num: '02', title: 'Global Impact', description: 'Work on projects that span continents and shape economies' },
    { num: '03', title: 'Innovation', description: 'Be part of a team that embraces cutting-edge solutions' }
  ]
};

const Careers = () => {
  const { content: cms } = useContent('careers', CAREERS_FALLBACK);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', linkedin_profile: '', position: ''
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setResume(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !resume) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('linkedin_profile', formData.linkedin_profile);
    submitData.append('position', formData.position);
    submitData.append('resume', resume);

    try {
      await axios.post(`${BACKEND_URL}/api/careers/apply`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Application submitted successfully!');
      setFormData({ name: '', email: '', phone: '', linkedin_profile: '', position: '' });
      setResume(null);
      document.getElementById('resume').value = '';
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-5 py-4 bg-white/5 border border-white/10 rounded text-white placeholder-white/25 focus:outline-none focus:border-[#17B9BE]/50 transition-colors text-sm";

  const perks = cms.perks || CAREERS_FALLBACK.perks;

  return (
    <div className="bg-black text-white" data-testid="careers-page">
      <SEO title="Careers" description={cms.hero_description} url="/careers" />
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0a0a0a] to-black" />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-6">Careers</p>
            <h1 className="font-display text-5xl md:text-7xl font-bold mb-6" data-testid="careers-title">
              {cms.hero_title_line1}<br />
              <span className="brand-gradient-text">{cms.hero_title_highlight}</span>
            </h1>
            <p className="text-lg text-white/50 max-w-3xl mx-auto">
              {cms.hero_description}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {/* Why Work */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
                Why <span className="brand-gradient-text">ARVARON?</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {perks.map((perk, index) => (
                <div key={index} className="bg-[#0a0a0a] p-8 rounded border border-white/5 hover:border-[#17B9BE]/20 transition-all duration-300">
                  <div className="text-xs text-[#17B9BE] font-mono mb-4">{perk.num}</div>
                  <h3 className="text-xl font-semibold mb-3">{perk.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{perk.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-[#0a0a0a] p-10 rounded border border-white/5">
              <h2 className="font-display text-3xl font-bold mb-3">Submit Your Application</h2>
              <p className="text-white/40 mb-10 text-sm">
                We're always looking for talented individuals. Submit your application and we'll get in touch.
              </p>

              <form onSubmit={handleSubmit} data-testid="careers-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="name" className="block text-xs uppercase tracking-wider text-white/40 mb-2">Full Name *</label>
                    <input type="text" id="name" name="name" data-testid="careers-name-input" value={formData.name} onChange={handleChange} className={inputClass} required />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-wider text-white/40 mb-2">Email *</label>
                    <input type="email" id="email" name="email" data-testid="careers-email-input" value={formData.email} onChange={handleChange} className={inputClass} required />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="phone" className="block text-xs uppercase tracking-wider text-white/40 mb-2">Phone *</label>
                    <input type="tel" id="phone" name="phone" data-testid="careers-phone-input" value={formData.phone} onChange={handleChange} className={inputClass} required />
                  </div>
                  <div>
                    <label htmlFor="linkedin_profile" className="block text-xs uppercase tracking-wider text-white/40 mb-2">LinkedIn Profile</label>
                    <input type="url" id="linkedin_profile" name="linkedin_profile" data-testid="careers-linkedin-input" value={formData.linkedin_profile} onChange={handleChange} placeholder="https://linkedin.com/in/yourprofile" className={inputClass} />
                  </div>
                </div>

                <div className="mb-5">
                  <label htmlFor="position" className="block text-xs uppercase tracking-wider text-white/40 mb-2">Position of Interest</label>
                  <input type="text" id="position" name="position" data-testid="careers-position-input" value={formData.position} onChange={handleChange} placeholder="e.g., Marketing Manager, Software Engineer" className={inputClass} />
                </div>

                <div className="mb-8">
                  <label htmlFor="resume" className="block text-xs uppercase tracking-wider text-white/40 mb-2">Resume/CV * (PDF, DOC, DOCX)</label>
                  <input type="file" id="resume" name="resume" data-testid="careers-resume-input" onChange={handleFileChange} accept=".pdf,.doc,.docx"
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded text-white focus:outline-none focus:border-[#17B9BE]/50 transition-colors text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#17B9BE] file:text-black hover:file:opacity-80"
                    required
                  />
                </div>

                <button
                  type="submit"
                  data-testid="careers-submit-button"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 brand-gradient-bg text-black font-semibold rounded-sm hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm uppercase tracking-wider"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
